import { useEffect, useState } from "react";
import { FaMoon, FaSun } from "react-icons/fa";

const STORAGE_KEY = "theme";

// Read the theme the anti-flash script (in index.html) already applied to <html>.
// Falls back to "light" so the default matches the base :root palette.
function getInitialTheme() {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
  }
  return "light";
}

// Toggle button that flips the site between light and dark, persisting the
// choice to localStorage so it survives reloads and future visits (until the
// user clears storage or switches it back).
export default function ThemeToggle({ className = "" }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage may be unavailable (privacy mode); theme still applies for
      // this session, it just won't persist.
    }
  }, [theme]);

  const isDark = theme === "dark";
  const toggle = () => {
    // Briefly enable color transitions so the whole page crossfades between
    // themes, then remove the class so it doesn't affect anything else.
    const root = document.documentElement;
    root.classList.add("theme-transition");
    window.clearTimeout(root._themeTransitionTimer);
    root._themeTransitionTimer = window.setTimeout(
      () => root.classList.remove("theme-transition"),
      500
    );
    setTheme(isDark ? "light" : "dark");
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={isDark}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={`grid place-items-center w-9 h-9 rounded-full text-text transition-colors hover:bg-surface hover:text-accent noSelect ${className}`}
    >
      {isDark ? <FaSun className="w-4 h-4" /> : <FaMoon className="w-4 h-4" />}
    </button>
  );
}
